import React, { useEffect, useState } from 'react';
import { TableComponent } from '../../../components/TableComponent';
import { IModalWindow, ModalWindow } from '../../../components/modal/ModalWindow';
import { useMerchants } from '../../../store/merchnts/merchants-context';
import { AddNewSite } from '../../../components/merchants/sites/AddNewSite';
import { SitesTableRow } from '../../../components/merchants/sites/SitesTableRow';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { Toggle } from 'rsuite';
import { SitesCardView } from '../../../components/merchants/sites/SitesCardView';
import { fetchMerchantSites, removeSite } from '../../../store/merchnts/actions/sites';
import { useParams } from 'react-router-dom';
import { IAuthState } from '../../../redux/AuthRedux';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../../setup';
import { parseJwt } from '../../login/Login';
import { checkRoles, CLAIMS } from '../../../utils/roles-helper';

const column = [
	{
		key: 'name',
		title: 'SITES.SITE_NAME',
		additionalColumnClassName: 'w-200px',
		claim: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN,
		showForMerchant: true
	},
	{
		key: 'descriptor',
		title: 'SITES.DESCRIPTOR',
		additionalColumnClassName: 'w-100px',
		claim: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN,
		showForMerchant: true
	},
	{
		key: 'email',
		title: 'SITES.CUSTOMER_EMAIL',
		additionalColumnClassName: 'w-150px',
		claim: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN,
		showForMerchant: true
	},
	{
		key: 'phone',
		title: 'SITES.CUSTOMER_PHONE',
		additionalColumnClassName: 'w-150px',
		claim: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN,
		showForMerchant: true
	},
	{
		key: 'url',
		title: 'SITES.CUSTOMER_URL',
		additionalColumnClassName: 'id-width',
		claim: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN,
		showForMerchant: true
	},
	{
		key: 'configurations',
		title: 'SITES.CONFIGURATIONS',
		additionalColumnClassName: 'w-250px',
		claim: CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN,
		showForMerchant: false
	},
	{
		key: 'view',
		title: 'SITES.WHITELISTED_URLS',
		additionalColumnClassName: 'w-100px',
		claim: CLAIMS.SUPER_ADMIN_AND_ALL_MERCHANTS_AND_PARTNER_ADMIN,
		showForMerchant: true
	},
	{
		key: 'action',
		title: 'SITES.ACTION',
		additionalColumnClassName: 'w-50px',
		claim: CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN,
		showForMerchant: false
	}
];

export interface ISitesProps {
	id: string,
	name: string,
	descriptor: string,
	descriptorCity: string,
	customerSupportName: string,
	customerSupportEmail: string,
	customerSupportPhone: string,
	customerSupportLink: string,
	whitelistedUrls: string[]
}

export const MerchantSitesPage = (): JSX.Element => {
	const { merchantState: { sitesList, isSitesListLoading }, dispatch } = useMerchants();
	const authUser: IAuthState = useSelector<RootState>(({ auth }) => auth, shallowEqual);
	const user = parseJwt(authUser.idToken);
	const mid = user?.['custom:mid'];
	const { id } = useParams<{ id: string }>();
	const merchantId = checkRoles(authUser, CLAIMS.MERCHANT_ADMIN) ? mid : id;
	const { t } = useTranslation();
	const [isChecked, setIsChecked] = useState<boolean>(false);
	const [modalState, setModalState] = useState<IModalWindow>({
		isOpen: false
	});

	useEffect(() => {
		void fetchMerchantSites(dispatch, merchantId);
	}, []);

	const openModal = (): void => {
		setModalState({
			isOpen: true,
			modalTitle: t('SITES.MODAL_TITLE'),
			confirmButtonText: 'SITES',
			size: 'md',
			minHeight: 500,
			content: <AddNewSite merchantId={merchantId} closeModal={closeModal}/>
		});
	};

	const closeModal = (): void => setModalState(prev => ({ ...prev, isOpen: false }));

	const deleteSite = (siteId: string): void => {
		if (siteId) {
			setModalState({
				isOpen: true,
				modalTitle: t('SITES.MODAL_DELETE_TITLE'),
				modalText: t('SITES.MODAL_DELETE_TEXT'),
				confirmButtonText: t('SITES.MODAL_DELETE_BTN_TEXT'),
				onConfirm: async () => {
					await removeSite(merchantId, siteId);
					await fetchMerchantSites(dispatch, merchantId);
					closeModal();
				},
				onCancel: closeModal
			});
		}
	};

	const editInformation = (siteInfo: ISitesProps): void => {
		setModalState({
			isOpen: true,
			modalTitle: t('SITES.MODAL_EDIT_TITLE'),
			size: 'md',
			contentClassName: 'd-block',
			content: <AddNewSite merchantId={merchantId} closeModal={closeModal} chosenSite={siteInfo}/>
		});
	};

	const tableColumns = column.filter(({ claim }) => checkRoles(authUser, claim));

	const table = (): JSX.Element => (
		<div className="card mb-5 mb-xl-8">
			<TableComponent
				column={tableColumns}
				tableData={sitesList}
				tableMinHeight={sitesList.length ? '' : 'table-min-height'}
				getRowComponent={(itemProps: ISitesProps): JSX.Element => (
					<SitesTableRow
						{...itemProps}
						key={itemProps.id}
						onDelete={deleteSite}
						onEdit={editInformation}
					/>
				)}
				title={t('SITES.TABLE_TITLE')}
				buttonName={checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN) && t('SITES.TABLE_BTN')}
				onButtonClick={openModal}
				isDataLoading={isSitesListLoading}
			/>
		</div>
	);

	return (
		<>
			<div className="d-flex align-items-center justify-content-end mb-15 mb-md-10 me-10">
				<div className="me-5 fw-bolder fs-6">List View</div>
				<Toggle
					size="md"
					checked={isChecked}
					className={clsx(isChecked ? 'is-active' : '')}
					onChange={(): void => setIsChecked(prev => !prev)}
				/>
				<div className="ms-5 fw-bolder fs-6">Card View</div>
			</div>
			{
				isChecked
					? <SitesCardView
						openModal={openModal}
						onEdit={editInformation}
						deleteSite={deleteSite}
						isAdmin={checkRoles(authUser, CLAIMS.SUPER_ADMIN_AND_ONLY_PARTNER_ADMIN)}
						isIntegrationPartner={checkRoles(authUser, CLAIMS.INTEGRATION_PARTNER_ADMIN)}
					/>
					: table()
			}

			<ModalWindow
				{...modalState}
				onClose={closeModal}
				backdrop="static"
			/>

		</>
	);
};
